import type { z } from 'zod'
import type { CommandType } from './protocol'

/**
 * Payload schemas per command type. A feature registers its schema once (module side effect in
 * its `commands.ts`), and `enqueueCommand` validates every payload before it reaches the outbox —
 * a malformed command caught on the phone is far cheaper than one rejected after the farmer left.
 *
 *   // src/features/gate/commands.ts
 *   export const openSessionPayload = z.object({ code: z.string(), ... })
 *   registerCommand('open_session', openSessionPayload)
 */
const schemas = new Map<CommandType, z.ZodType>()

export function registerCommand<S extends z.ZodType>(type: CommandType, schema: S): S {
  schemas.set(type, schema)
  return schema
}

export function getCommandSchema(type: CommandType): z.ZodType | undefined {
  return schemas.get(type)
}

export class InvalidCommandPayloadError extends Error {
  override readonly name = 'InvalidCommandPayloadError'
  readonly type: CommandType
  readonly issues: z.core.$ZodIssue[]

  constructor(type: CommandType, issues: z.core.$ZodIssue[]) {
    super(
      `Invalid payload for ${type}: ${issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')}`,
    )
    this.type = type
    this.issues = issues
  }
}

/** Validate (and normalise, e.g. defaults) a payload. Unregistered types pass through unchanged. */
export function validatePayload(type: CommandType, payload: unknown): unknown {
  const schema = schemas.get(type)
  if (!schema) return payload
  const parsed = schema.safeParse(payload)
  if (!parsed.success) throw new InvalidCommandPayloadError(type, parsed.error.issues)
  return parsed.data
}
